import { IdentifierKeyMap, TIdentifierKey, TIdentifierObject, TIdentifierValue } from "@/constants/Test";
import { TShareableInfo } from "@/type/axios";
import { ITabConfig } from "@/type/router";
import { refresh } from "@redux/slice/api";
import { useAppDispatch, useAppSelector } from "@redux/store";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "react-router-dom";

export function useRefresh(fn: () => Promise<any>) {
  const isRefresh = useAppSelector(state => state.api.refresh);
  const dispatch = useAppDispatch();
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (isRefresh) {
      hasLoadedRef.current = true;
      // catch: user-facing error surfacing is the axios layer's job (useSystemMessage); here a
      // rejection must not escape, and above all must not skip resetting the flag — a flag stuck
      // at true turns every future refresh(true) dispatch into a no-op for all useRefresh consumers.
      fn()
        .catch(() => undefined)
        .finally(() => {
          dispatch(refresh(false));
        });
    } else if (!hasLoadedRef.current) {
      // The refresh flag is a one-shot pulse consumed by whichever consumers are mounted when it
      // fires. Lazy-loaded tabs (Suspense chunks) can mount after an earlier component already
      // consumed the navigation pulse — e.g. deep-linking to /Plan/TestWaterfall#TIMELINE_DETAILS —
      // so they must still perform their initial load.
      hasLoadedRef.current = true;
      fn().catch(() => undefined);
    }
  }, [isRefresh]);
}

/**
 * Dispatches the global refresh pulse on navigation (path / key / search / hash change),
 * but NOT on the initial mount — the first page load is already handled by each page's
 * useRefresh mount path, so pulsing on mount too makes every consumer fetch twice.
 * Mounted once by MainSider.
 */
export function useNavigationRefreshPulse(location: { pathname: string; key?: string; search: string; hash: string }) {
  const dispatch = useAppDispatch();
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    dispatch(refresh(true));
  }, [location.pathname, location.key, location.search, location.hash]);
}

// async effect
// eslint-disable-next-line no-unused-vars
export function useAsyncEffect(effect: (isMounted: () => boolean) => unknown | Promise<unknown>, input?: any[], delay?: number): void;
// eslint-disable-next-line no-redeclare
// eslint-disable-next-line no-unused-vars
export function useAsyncEffect<V>(effect: (isMounted: () => boolean) => V | Promise<V>, destroy?: (result?: V) => void, inputs?: any[]): void;
// eslint-disable-next-line no-redeclare
export function useAsyncEffect(effect: any, destroy?: any, inputs?: any): void {
  const hasDestroy = typeof destroy === "function";

  useEffect(
    function () {
      let mounted = true;
      let result: any;
      const maybePromise = effect(function () {
        return mounted;
      });
      Promise.resolve(maybePromise).then(function (value) {
        result = value;
      });

      return function () {
        mounted = false;
        if (hasDestroy) {
          destroy(result);
        }
      };
    },
    hasDestroy ? inputs : destroy,
  );
}

/**
 * Custom hook to get current page identifier from URL search parameters
 * @param requiredValues - Identifier values to extract (e.g., "projectId", "buildId"). If empty/undefined, returns all found identifier values.
 *                         If provided, validates that URL contains values for all specified identifiers.
 * @returns Object containing identifier values extracted from URL params
 * @throws Error if requiredValues are specified but URL doesn't contain values for those identifiers
 *
 * @example
 * // Get specific identifiers (validates that URL has these values)
 * usePageIdentifier(["projectId", "buildId", "phaseId"])
 * // Returns: { projectId: "...", buildId: "...", phaseId: "..." }
 *
 * @example
 * // Get all found identifiers (no validation)
 * usePageIdentifier()
 * // Returns all identifier properties found in URL
 */
const DEFAULT_REQUIRED_VALUES: TIdentifierValue[] = [IdentifierKeyMap.PROJECT, IdentifierKeyMap.TEST_LOCATION, IdentifierKeyMap.PHASE, IdentifierKeyMap.BUILD];

export function usePageIdentifier(requiredValues: TIdentifierValue[] = DEFAULT_REQUIRED_VALUES): TIdentifierObject {
  const [searchParams] = useSearchParams();
  const shareableInfo = useLoaderData<TShareableInfo>();

  const finalIdentifier = useMemo(() => {
    const newIdentifier: TIdentifierObject = {};
    const validIdentifierValues = Object.values(IdentifierKeyMap) as TIdentifierValue[];

    // Extract identifiers from URL search params (URL uses TIdentifierValue like "projectId", "phaseId")
    searchParams.forEach((value, key) => {
      // Check if key is a valid TIdentifierValue (e.g., "projectId", "phaseId", "buildId")
      if (validIdentifierValues.includes(key as TIdentifierValue)) {
        newIdentifier[key as TIdentifierValue] = value;
      }
    });

    // If no identifiers found in URL, fallback to shareableInfo
    if (Object.keys(newIdentifier).length === 0 && shareableInfo?.identifier) {
      // Convert shareableInfo.identifier (TIdentifierKey based) to TIdentifierValue based
      const convertedIdentifier: TIdentifierObject = {};
      Object.entries(shareableInfo.identifier).forEach(([key, value]) => {
        const identifierValue = IdentifierKeyMap[key as TIdentifierKey];
        if (identifierValue && value) {
          convertedIdentifier[identifierValue] = value;
        }
      });

      // If requiredValues specified, validate converted identifier has those values
      if (requiredValues && requiredValues.length > 0) {
        const missingValues: TIdentifierValue[] = [];
        for (const val of requiredValues) {
          if (!convertedIdentifier[val]) {
            missingValues.push(val);
          }
        }
        if (missingValues.length > 0) {
          throw new Error(`Missing required identifier values in URL: ${missingValues.join(", ")}`);
        }
      }
      return convertedIdentifier;
    }
    return newIdentifier;
  }, [searchParams, requiredValues, shareableInfo]);

  return finalIdentifier;
}

/**
 * Type guard to check if input is ITabConfig[]
 */
function isTabConfigArray(input: Record<string, string> | ITabConfig[]): input is ITabConfig[] {
  return Array.isArray(input) && input.length > 0 && typeof input[0].key === "string" && typeof input[0].label === "string";
}

/**
 * Custom hook to read and write tab state from URL hash
 * Format: /path?query#TabName
 * @param tabConfig - Either:
 *   1. Object containing tab key mappings (e.g., { TAB_A: "tabA", TAB_B: "tabB" }) - Legacy format
 *   2. Array of ITabConfig from Redux (e.g., [{ key: "tabA", label: "tabs.tabA" }]) - New format
 * The first key's value will be used as the default tab
 * @returns [currentTab, setTab] - Current tab from hash and setter function
 */
// eslint-disable-next-line no-unused-vars
export function useTabHash<T extends Record<string, string>>(tabConfig: T): [T[keyof T], (tab: T[keyof T]) => void];
// eslint-disable-next-line no-unused-vars
export function useTabHash(tabConfig: ITabConfig[]): [string, (tab: string) => void];
// eslint-disable-next-line no-redeclare, no-unused-vars
export function useTabHash<T extends Record<string, string>>(tabConfig: T | ITabConfig[]): [string, (tab: string) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract valid tab values based on input format
  const validTabs = useMemo(() => {
    if (isTabConfigArray(tabConfig)) {
      return tabConfig.map(tab => tab.key);
    }
    return Object.values(tabConfig);
  }, [tabConfig]);

  // Use the first key's value as the default tab
  const defaultTab = useMemo(() => {
    if (isTabConfigArray(tabConfig)) {
      return tabConfig[0]?.key ?? "";
    }
    return Object.values(tabConfig)[0] ?? "";
  }, [tabConfig]);

  // Extract tab from hash (removes the leading #)
  const currentTab = useMemo(() => {
    const hash = location.hash.slice(1); // Remove leading #
    if (!hash) return defaultTab;
    // Validate against valid tabs from the map
    if (!validTabs.includes(hash)) {
      return defaultTab;
    }
    return hash;
  }, [location.hash, defaultTab, validTabs]);

  // Set tab by updating URL hash
  const setTab = useCallback(
    (tab: string) => {
      // Preserve existing pathname and search params, only update hash
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: tab,
        },
        { replace: false },
      );
    },
    [navigate, location.pathname, location.search],
  );

  return [currentTab, setTab];
}
